import { Controller } from "@hotwired/stimulus"

// Handles cascading FBR API dropdowns in the product form:
//   Sale Type → rates dropdown
//   HS Code   → UOM dropdown
//   Rate       → SRO Schedule dropdown
//   SRO       → SRO Item dropdown
export default class extends Controller {
  static targets = [
    "saleTypeSelect",
    "transactionTypeId",
    "hsCode",
    "uomSelect",
    "sellingUom",
    "conversionSection",
    "conversionSellingQty",
    "conversionFbrQty",
    "conversionSellingLabel",
    "conversionFbrLabel",
    "conversionPrompt",
    "packBaseLabel",
    "rateSelect",
    "rateId",
    "rateDesc",
    "taxRateHidden",
    "sroScheduleSection",
    "sroScheduleSelect",
    "sroItemSection",
    "sroItemSelect",
    "spinner",
    "itemTypeSelect",
    "inventorySection",
    "supplierSection"
  ]

  connect() {
    // If editing an existing product, populate dropdowns from stored values
    const existingTransTypeId = this.transactionTypeIdTarget.value
    const existingRateId      = this.rateIdTarget.value
    const existingSroSchedule = this.sroScheduleSelectTarget.dataset.selected
    const existingHsCode      = this.hsCodeTarget.value.trim()

    if (existingTransTypeId) {
      this.loadRates(existingTransTypeId, existingRateId)
    }
    if (existingHsCode) {
      this.loadUom(existingHsCode)
    }

    this.toggleInventorySections()
    this.toggleConversionSection()
    this.updatePackBaseLabels()
  }

  onItemTypeChange() {
    this.toggleInventorySections()
  }

  // ── Selling unit vs FBR unit ───────────────────────────────────────────────
  // FBR validates uoM against the HS code, so the unit a product must be reported in
  // may not be the unit it is sold in — in either direction (pieces sold against a
  // Kilogram HS code, or KG and Litres sold against a "Numbers, pieces, units" one).
  // When the two differ we need a ratio to convert with.

  onUomChange() {
    this.toggleConversionSection()
    this.updatePackBaseLabels()
  }

  // A pack row reads "1 Box = 12 <base unit>". The base unit is whatever this product
  // is sold in, so the label has to follow the Selling Unit field as it is typed —
  // otherwise the row says "12 units" while the invoice will say "12 Singles".
  updatePackBaseLabels() {
    if (!this.hasPackBaseLabelTarget) return

    const selling = this.hasSellingUomTarget ? this.sellingUomTarget.value : ""
    const fbr     = this.hasUomSelectTarget ? this.uomSelectTarget.value : ""
    const label   = this.humanize(selling) || this.humanize(fbr) || "units"

    this.packBaseLabelTargets.forEach(el => { el.textContent = label })
  }

  toggleConversionSection() {
    if (!this.hasConversionSectionTarget) return

    const fbrUom     = this.uomSelectTarget.value
    const sellingUom = this.hasSellingUomTarget ? this.sellingUomTarget.value : ""
    const needed     = this.unitsDiffer(sellingUom, fbrUom)

    this.conversionSectionTarget.classList.toggle("hidden", !needed)
    // Only the FBR side is required — an empty left side means one, matching
    // Product#normalize_uom_conversion.
    if (this.hasConversionFbrQtyTarget) this.conversionFbrQtyTarget.required = needed
    if (needed) {
      this.updateConversionLabels(sellingUom, fbrUom)
      this.suggestCountRatio(sellingUom, fbrUom)
    }
  }

  // A gross is 144 of whatever it counts and a dozen is 12 — definitional, not
  // product-specific, so the ratio can be filled in for the seller when FBR wants the
  // count. Only ever fills a blank field: a number already there was either typed or
  // saved, and both outrank a guess. Sizes come from Product::COUNT_UNIT_SIZES so the
  // list has one home. Nothing is suggested when FBR wants a weight or a volume — how
  // much a gross of buttons weighs is knowable only to the seller.
  suggestCountRatio(sellingUom, fbrUom) {
    if (!this.hasConversionFbrQtyTarget || this.conversionFbrQtyTarget.value) return
    if (this.normalizeUom(fbrUom) !== "numberspiecesunits") return

    const sizes = this.countUnitSizes()
    const size  = sizes[(sellingUom || "").trim().toLowerCase()]
    if (!size) return

    this.conversionFbrQtyTarget.value = size
    if (this.hasConversionSellingQtyTarget && !this.conversionSellingQtyTarget.value) {
      this.conversionSellingQtyTarget.value = 1
    }
  }

  countUnitSizes() {
    try {
      return JSON.parse(this.conversionSectionTarget.dataset.countUnitSizes || "{}")
    } catch (e) {
      return {}
    }
  }

  // The row reads as a sentence — "1 Piece = 1.5 Kilogram" — so the labels have to
  // track whatever units are currently selected, in whichever order they run.
  updateConversionLabels(sellingUom, fbrUom) {
    if (this.hasConversionSellingLabelTarget) {
      this.conversionSellingLabelTarget.textContent = this.humanize(sellingUom) || "selling unit"
    }
    if (this.hasConversionFbrLabelTarget) {
      this.conversionFbrLabelTarget.textContent = this.humanize(fbrUom) || "FBR unit"
    }
    if (this.hasConversionPromptTarget) {
      const selling = this.singularize(sellingUom)
      const fbr     = this.humanize(fbrUom)
      this.conversionPromptTarget.textContent =
        `FBR requires this product to be reported in ${fbr}, but you sell it in ` +
        `${this.humanize(sellingUom)}. How many ${fbr} is one ${selling}? ` +
        `If that number is awkward, put 1 on the ${fbr} side instead and say how many ` +
        `${this.humanize(sellingUom)} that is.`
    }
  }

  // Compare loosely: FBR's reference data varies in case and punctuation
  // ("Kilogram" vs "KG", "Numbers, pieces, units"). Note this is a sameness test, not a
  // conversion table — "Gross" and "Pieces" are genuinely different units (144 to 1),
  // so they must keep comparing as different and go on to demand a ratio.
  unitsDiffer(sellingUom, fbrUom) {
    const a = this.normalizeUom(sellingUom)
    const b = this.normalizeUom(fbrUom)
    return a !== "" && b !== "" && a !== b
  }

  normalizeUom(value) {
    return (value || "").toLowerCase().replace(/[^a-z0-9]/g, "")
  }

  // FBR's registry phrasing reads fine as a unit word except this entry, which is a
  // category description — "1 Numbers, pieces, units" is not a sentence. Mirrors
  // DocumentFormatting::UOM_DISPLAY_LABELS.
  humanize(uom) {
    const raw = (uom || "").trim()
    return raw.toLowerCase() === "numbers, pieces, units" ? "Pieces" : raw
  }

  // "Numbers, pieces, units" → "piece", "Kilogram" → "kilogram". Good enough for a
  // prompt; falls back to the raw string when the unit isn't a comma-separated label.
  singularize(uom) {
    const parts = (uom || "").split(",").map(s => s.trim()).filter(Boolean)
    const word  = (parts.length > 1 ? parts[1] : parts[0] || "unit").toLowerCase()

    // A double s is never a plural ending: "Gross" is already singular, and stripping
    // the s asks the seller how many pieces are in one "gros".
    if (word.endsWith("ss")) return word
    if (/(?:x|s|z|ch|sh)es$/.test(word)) return word.slice(0, -2)   // boxes -> box
    return word.replace(/s$/, "")
  }

  toggleInventorySections() {
    const isInventory = this.itemTypeSelectTarget.value === "inventory"
    this.inventorySectionTargets.forEach(el => el.classList.toggle("hidden", !isInventory))
    this.supplierSectionTargets.forEach(el => el.classList.toggle("hidden", !isInventory))
  }

  // ── Sale Type changed ──────────────────────────────────────────────────────

  onSaleTypeChange(event) {
    const select   = event.target
    const option   = select.options[select.selectedIndex]
    const typeId   = option?.dataset?.typeId || ""
    const typeName = select.value

    this.transactionTypeIdTarget.value = typeId
    this.clearRateDropdown()
    this.clearSroDropdowns()

    if (typeId) this.loadRates(typeId, null)
  }

  // ── HS Code blurred / changed ──────────────────────────────────────────────

  onHsCodeChange(event) {
    const hs = event.target.value.trim()
    this.clearUomDropdown()
    if (hs.length >= 4) this.loadUom(hs)
  }

  // ── Rate changed ───────────────────────────────────────────────────────────

  onRateChange(event) {
    const select = event.target
    const option = select.options[select.selectedIndex]
    const rateId    = option?.dataset?.rateId    || ""
    const rateValue = option?.dataset?.rateValue || ""
    const rateDesc  = option?.value?.trim()      || ""

    this.rateIdTarget.value       = rateId
    this.taxRateHiddenTarget.value = rateValue
    if (this.hasRateDescTarget) this.rateDescTarget.value = rateDesc

    this.clearSroDropdowns()
    if (rateId) this.loadSroSchedules(rateId)
  }

  // ── SRO Schedule changed ───────────────────────────────────────────────────

  onSroScheduleChange(event) {
    const select = event.target
    const option = select.options[select.selectedIndex]
    const sroId  = option?.dataset?.sroId || ""
    this.sroItemSectionTarget.classList.remove("hidden")
    this.clearSelect(this.sroItemSelectTarget, sroId ? "Loading…" : "Select SRO schedule first…")
    if (sroId) this.loadSroItems(sroId, null)
  }

  // ── API fetchers ────────────────────────────────────────────────────────────

  async loadRates(transTypeId, selectedRateId) {
    this.showSpinner()
    try {
      const res  = await fetch(`/fbr/rates?trans_type_id=${encodeURIComponent(transTypeId)}`)
      const data = await res.json()

      const select = this.rateSelectTarget
      this.clearSelect(select, "Select rate...")

      let matchedRateDesc = ""
      data.forEach(rate => {
        const opt = document.createElement("option")
        opt.value           = rate.label
        opt.dataset.rateId  = rate.id
        opt.dataset.rateValue = rate.value
        opt.text            = rate.label
        if (String(rate.id) === String(selectedRateId)) {
          opt.selected = true
          matchedRateDesc = rate.label || ""
        }
        select.appendChild(opt)
      })
      if (this.hasRateDescTarget) this.rateDescTarget.value = matchedRateDesc

      // If we pre-selected a rate, also load SRO schedules
      if (selectedRateId) {
        const existingSroSchedule = this.sroScheduleSelectTarget.dataset.selected
        this.loadSroSchedules(selectedRateId, existingSroSchedule)
      }
    } catch (e) {
      console.error("Failed to load rates:", e)
    } finally {
      this.hideSpinner()
    }
  }

  async loadUom(hsCode) {
    this.showSpinner()
    try {
      const res  = await fetch(`/fbr/uom?hs_code=${encodeURIComponent(hsCode)}`)
      const data = await res.json()

      const select = this.uomSelectTarget
      this.clearSelect(select, "Select UOM...")

      const storedUom = select.dataset.selected || ""

      data.forEach(uom => {
        const opt = document.createElement("option")
        opt.value = uom.description
        opt.text  = uom.description
        if (uom.description === storedUom) opt.selected = true
        select.appendChild(opt)
      })

      // Auto-select if only one result
      if (data.length === 1) select.value = data[0].description

      // The FBR unit is only known once these options land, so the conversion row
      // can't be decided until now.
      this.toggleConversionSection()
    } catch (e) {
      console.error("Failed to load UOM:", e)
    } finally {
      this.hideSpinner()
    }
  }

  async loadSroSchedules(rateId, selectedSroDescription) {
    this.showSpinner()
    try {
      const res  = await fetch(`/fbr/sro_schedules?rate_id=${encodeURIComponent(rateId)}`)
      const data = await res.json()

      const section = this.sroScheduleSectionTarget
      const select  = this.sroScheduleSelectTarget

      if (data.length === 0) {
        section.classList.add("hidden")
        this.sroItemSectionTarget.classList.add("hidden")
        return
      }

      this.clearSelect(select, "None (leave blank)")
      let matchedSroId = null
      data.forEach(sro => {
        const opt = document.createElement("option")
        opt.value        = sro.description
        opt.dataset.sroId = sro.id
        opt.text         = sro.description
        if (sro.description === selectedSroDescription) {
          opt.selected = true
          matchedSroId = sro.id
        }
        select.appendChild(opt)
      })

      section.classList.remove("hidden")
      this.sroItemSectionTarget.classList.remove("hidden")
      this.clearSelect(this.sroItemSelectTarget, "Select SRO schedule first…")

      if (matchedSroId) {
        const existingSroItem = this.sroItemSelectTarget.dataset.selected
        this.loadSroItems(matchedSroId, existingSroItem)
      }
    } catch (e) {
      console.error("Failed to load SRO schedules:", e)
    } finally {
      this.hideSpinner()
    }
  }

  async loadSroItems(sroId, selectedItemDescription) {
    this.showSpinner()
    try {
      const res  = await fetch(`/fbr/sro_items?sro_id=${encodeURIComponent(sroId)}`)
      const data = await res.json()

      const section = this.sroItemSectionTarget
      const select  = this.sroItemSelectTarget

      if (data.length === 0) {
        this.clearSelect(select, "No items for this schedule")
        return
      }

      this.clearSelect(select, "None (leave blank)")
      data.forEach(item => {
        const opt = document.createElement("option")
        opt.value = item.description
        opt.text  = item.description
        if (item.description === selectedItemDescription) opt.selected = true
        select.appendChild(opt)
      })

      section.classList.remove("hidden")
    } catch (e) {
      console.error("Failed to load SRO items:", e)
    } finally {
      this.hideSpinner()
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  clearSelect(select, placeholder) {
    select.innerHTML = `<option value="">${placeholder}</option>`
  }

  clearRateDropdown() {
    this.rateIdTarget.value       = ""
    this.taxRateHiddenTarget.value = ""
    if (this.hasRateDescTarget) this.rateDescTarget.value = ""
    this.clearSelect(this.rateSelectTarget, "Select rate...")
  }

  clearUomDropdown() {
    this.clearSelect(this.uomSelectTarget, "Select UOM...")
  }

  clearSroDropdowns() {
    this.sroScheduleSectionTarget.classList.add("hidden")
    this.sroItemSectionTarget.classList.add("hidden")
    this.clearSelect(this.sroScheduleSelectTarget, "None (leave blank)")
    this.clearSelect(this.sroItemSelectTarget, "None (leave blank)")
  }

  showSpinner() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden")
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
  }
}
